import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Pencil, Square, Circle, Type, StickyNote, Upload, FileText,
  Download, Trash2, Move, MousePointer, Minus, Save, Undo, Redo,
  ArrowLeft, Plus, Search, Calendar, Edit3, Loader
} from 'lucide-react';
import axiosInstance from '@/axios';


const Notes = () => {
  const [currentView, setCurrentView] = useState('list'); 
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [savedNotes, setSavedNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);


  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [isDrawing, setIsDrawing] = useState(false);
  const [elements, setElements] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [stickyNotes, setStickyNotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [currentNoteTitle, setCurrentNoteTitle] = useState('');

  // Colors and styles
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fillColor, setFillColor] = useState('transparent');



  // Load notes from API
  const loadNotes = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/notes');
      if (response.data) {
        setSavedNotes(Array.isArray(response?.data?.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
      alert('Failed to load notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load notes on component mount
  useEffect(() => {
    loadNotes();
  }, []);

  // Filter notes based on search term
  const filteredNotes = Array.isArray(savedNotes)
  ? savedNotes.filter(note =>
      note.title && note.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  : [];
    console.log('Filtered Notes:', filteredNotes);
  // Search notes via API
  const searchNotes = async (query) => {
    if (!query.trim()) {
      loadNotes();
      return;
    }

    setLoading(true);
    try {
      const response = axiosInstance.get(`/notes/search`, { params: { q: query } });
      if (response.data) {
        setSavedNotes(response.data.data || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchNotes(searchTerm);
      } else {
        loadNotes();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Create new note
  const createNewNote = async () => {
    try {
      const response = await axiosInstance.post('/notes', {
        title: 'Untitled Note',
        elements: [],
        stickyNotes: []
      });
      if (response.data) {
        const newNote = response.data.data;
        setSavedNotes([newNote, ...savedNotes]);
        setCurrentNoteId(newNote.id);
        setCurrentNoteTitle(newNote.title);
        setElements([]);
        setStickyNotes([]);
        setHistory([]);
        setHistoryIndex(-1);
        setCurrentView('whiteboard');
      }
    } catch (error) {
      console.error('Failed to create note:', error);
      alert('Failed to create note. Please try again.');
    }
  };

  // Open existing note
  const openNote = (note) => {
    setCurrentNoteId(note.id);
    setCurrentNoteTitle(note.title);
    setElements(note.elements || []);
    setStickyNotes(note.stickyNotes || []);
    setHistory([]);
    setHistoryIndex(-1);
    setCurrentView('whiteboard');
  };

  // Save current note
  const saveCurrentNote = async () => {
    console.log('Saving current note:', currentNoteId);
    if (!currentNoteId || saving) return;

    setSaving(true);
    try {
      const canvas = canvasRef.current;
      let thumbnail = null;
      if (canvas && elements.length > 0) {
        thumbnail = canvas.toDataURL('image/png', 0.3);
      }
      const response = await axiosInstance.put(`/notes/${currentNoteId}`, {
        title: currentNoteTitle,
        elements,
        stickyNotes,
        thumbnail
      });

      if (response.data) {
        setSavedNotes(prevNotes =>
          prevNotes.map(note =>
            note.id === currentNoteId
              ? response.data.data
              : note
          )
        );
      }
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setSaving(false);
    }
  };

  // Delete note
  const deleteNote = async (noteId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/notes/${noteId}`);
      setSavedNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Go back to notes list
  const backToList = async () => {
    if (currentNoteId) {
      console.log('Saving current note before going back...');
      console.log('currentNoteId:', currentNoteId);
      await saveCurrentNote();
    }
    console.log('Going back to notes list');
    setCurrentView('list');
    setCurrentNoteId(null);
    loadNotes(); // Refresh the list
  };

  // Save state to history
  const saveToHistory = useCallback(() => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ elements: [...elements], stickyNotes: [...stickyNotes] });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [elements, stickyNotes, history, historyIndex]);

  // Undo/Redo functionality
  const undo = () => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setElements(prevState.elements);
      setStickyNotes(prevState.stickyNotes);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setElements(nextState.elements);
      setStickyNotes(nextState.stickyNotes);
      setHistoryIndex(historyIndex + 1);
    }
  };

  // Get mouse position relative to canvas
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Handle mouse down
  const handleMouseDown = (e) => {
    const pos = getMousePos(e);

    if (tool === 'select') {
      const element = findElementAtPosition(pos.x, pos.y);
      if (element) {
        setSelectedElement(element);
        setDragOffset({
          x: pos.x - element.x,
          y: pos.y - element.y
        });
      } else {
        setSelectedElement(null);
      }
      return;
    }

    if (tool === 'text') {
      setTextPosition(pos);
      setShowTextInput(true);
      return;
    }

    if (tool === 'sticky') {
      const newSticky = {
        id: Date.now(),
        x: pos.x,
        y: pos.y,
        text: 'New Note',
        color: '#fef08a'
      };
      setStickyNotes([...stickyNotes, newSticky]);
      saveToHistory();
      return;
    }

    setIsDrawing(true);

    if (tool === 'pen') {
      setCurrentPath([pos]);
    } else {
      const newElement = {
        id: Date.now(),
        type: tool,
        x: pos.x,
        y: pos.y,
        x2: pos.x,
        y2: pos.y,
        strokeColor,
        strokeWidth,
        fillColor,
        path: tool === 'pen' ? [pos] : []
      };
      setElements([...elements, newElement]);
    }
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    const pos = getMousePos(e);

    if (tool === 'select' && selectedElement && isDrawing) {
      const newX = pos.x - dragOffset.x;
      const newY = pos.y - dragOffset.y;

      setElements(elements.map(el =>
        el.id === selectedElement.id
          ? { ...el, x: newX, y: newY }
          : el
      ));
      setSelectedElement({ ...selectedElement, x: newX, y: newY });
      return;
    }

    if (!isDrawing) return;

    if (tool === 'pen') {
      setCurrentPath([...currentPath, pos]);
    } else {
      setElements(elements.map(el =>
        el.id === elements[elements.length - 1]?.id
          ? { ...el, x2: pos.x, y2: pos.y }
          : el
      ));
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (isDrawing && tool === 'pen' && currentPath.length > 0) {
      const newElement = {
        id: Date.now(),
        type: 'pen',
        path: currentPath,
        strokeColor,
        strokeWidth
      };
      setElements([...elements, newElement]);
      setCurrentPath([]);
      saveToHistory();
    } else if (isDrawing && tool !== 'pen') {
      saveToHistory();
    }

    setIsDrawing(false);
  };

  // Find element at position
  const findElementAtPosition = (x, y) => {
    return elements.find(el => {
      if (el.type === 'pen') {
        return el.path.some(point =>
          Math.abs(point.x - x) < 10 && Math.abs(point.y - y) < 10
        );
      }
      return x >= Math.min(el.x, el.x2) && x <= Math.max(el.x, el.x2) &&
        y >= Math.min(el.y, el.y2) && y <= Math.max(el.y, el.y2);
    });
  };

  // Add text element
  const addTextElement = () => {
    if (textInput.trim()) {
      const newElement = {
        id: Date.now(),
        type: 'text',
        x: textPosition.x,
        y: textPosition.y,
        text: textInput,
        strokeColor,
        fontSize: 16
      };
      setElements([...elements, newElement]);
      saveToHistory();
    }
    setTextInput('');
    setShowTextInput(false);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const newElement = {
          id: Date.now(),
          type: 'image',
          x: 100,
          y: 100,
          src: event.target.result,
          width: 200,
          height: 150
        };
        setElements([...elements, newElement]);
        saveToHistory();
      };
      reader.readAsDataURL(file);
    }
  };

  // Take screenshot
  const takeScreenshot = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `${currentNoteTitle || 'whiteboard'}-screenshot.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  // Clear canvas
  const clearCanvas = () => {
    setElements([]);
    setStickyNotes([]);
    saveToHistory();
  };

  // Render canvas
  useEffect(() => {
    if (currentView !== 'whiteboard') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw elements
    elements.forEach(element => {
      ctx.strokeStyle = element.strokeColor || '#000000';
      ctx.lineWidth = element.strokeWidth || 2;
      ctx.fillStyle = element.fillColor || 'transparent';

      switch (element.type) {
        case 'pen':
          if (element.path && element.path.length > 1) {
            ctx.beginPath();
            ctx.moveTo(element.path[0].x, element.path[0].y);
            element.path.forEach(point => ctx.lineTo(point.x, point.y));
            ctx.stroke();
          }
          break;
        case 'rectangle':
          const width = element.x2 - element.x;
          const height = element.y2 - element.y;
          ctx.beginPath();
          ctx.rect(element.x, element.y, width, height);
          if (element.fillColor !== 'transparent') ctx.fill();
          ctx.stroke();
          break;
        case 'circle':
          const radius = Math.sqrt(Math.pow(element.x2 - element.x, 2) + Math.pow(element.y2 - element.y, 2));
          ctx.beginPath();
          ctx.arc(element.x, element.y, radius, 0, 2 * Math.PI);
          if (element.fillColor !== 'transparent') ctx.fill();
          ctx.stroke();
          break;
        case 'line':
          ctx.beginPath();
          ctx.moveTo(element.x, element.y);
          ctx.lineTo(element.x2, element.y2);
          ctx.stroke();
          break;
        case 'text':
          ctx.font = `${element.fontSize || 16}px Arial`;
          ctx.fillStyle = element.strokeColor;
          ctx.fillText(element.text, element.x, element.y);
          break;
        case 'image':
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, element.x, element.y, element.width, element.height);
          };
          img.src = element.src;
          break;
      }
    });

    if (currentPath.length > 1) {
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.beginPath();
      ctx.moveTo(currentPath[0].x, currentPath[0].y);
      currentPath.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.stroke();
    }
  }, [elements, currentPath, strokeColor, strokeWidth, currentView]);

  // Auto-save when elements or sticky notes change
  useEffect(() => {
    if (currentView === 'whiteboard' && currentNoteId) {
      const timeoutId = setTimeout(() => {
        saveCurrentNote();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [elements, stickyNotes, currentNoteTitle]);

  if (currentView === 'list') {
    return (
      <div className="h-screen  ">
        {/* Header */}
        <div className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">My Notes</h1>
            <button
              onClick={createNewNote}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              New Note
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4">
          <div className="relative max-w-md">
            <Search className="!absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {loading && (
              <Loader className="!absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" size={16} />
            )}
          </div>
        </div>

        {/* Notes Grid */}
        <div className="px-6 pb-6">
          {loading && savedNotes.length === 0 ? (
            <div className="text-center py-12">
              <Loader className="mx-auto text-gray-300 mb-4 animate-spin" size={64} />
              <p className="text-gray-500 text-lg">Loading notes...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredNotes.map(note => (
                <div
                  key={note.id}
                  onClick={() => openNote(note)}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  {/* Thumbnail */}
                  <div className="h-48 bg-gray-100 rounded-t-lg flex items-center justify-center overflow-hidden">
                    {note.thumbnail ? (
                      <img
                        src={note.thumbnail}
                        alt="Note thumbnail"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-gray-400">
                        <FileText size={48} />
                      </div>
                    )}
                  </div>

                  {/* Note Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 truncate">{note.title}</h3>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(note.updated_at ? note.updated_at : note.created_at).toLocaleDateString()}
                      </div>
                      <button
                        onClick={(e) => deleteNote(note.id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredNotes.length === 0 && (
            <div className="text-center py-12">
              <FileText size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No notes found matching your search' : 'No notes yet'}
              </p>
              <p className="text-gray-400 mt-2">
                {searchTerm ? 'Try a different search term' : 'Click "New Note" to create your first note'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* Toolbar */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={backToList}
            className="p-2 bg-gray-200 rounded hover:bg-gray-300 flex items-center gap-1"
          >
            <ArrowLeft size={20} />
          </button>
          <input
            type="text"
            value={currentNoteTitle}
            onChange={(e) => setCurrentNoteTitle(e.target.value)}
            className="text-lg font-semibold bg-transparent border-none outline-none focus:bg-gray-50 px-2 py-1 rounded"
            placeholder="Note title..."
          />
          {saving && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Loader size={16} className="animate-spin" />
              Saving...
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setTool('select')}
            className={`p-2 rounded ${tool === 'select' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <MousePointer size={20} />
          </button>
          <button
            onClick={() => setTool('pen')}
            className={`p-2 rounded ${tool === 'pen' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <Pencil size={20} />
          </button>
          <button
            onClick={() => setTool('rectangle')}
            className={`p-2 rounded ${tool === 'rectangle' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <Square size={20} />
          </button>
          <button
            onClick={() => setTool('circle')}
            className={`p-2 rounded ${tool === 'circle' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <Circle size={20} />
          </button>
          <button
            onClick={() => setTool('line')}
            className={`p-2 rounded ${tool === 'line' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <Minus size={20} />
          </button>
          <button
            onClick={() => setTool('text')}
            className={`p-2 rounded ${tool === 'text' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <Type size={20} />
          </button>
          <button
            onClick={() => setTool('sticky')}
            className={`p-2 rounded ${tool === 'sticky' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            <StickyNote size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="color"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
            className="w-8 h-8 rounded border"
          />
          <input
            type="range"
            min="1"
            max="20"
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(e.target.value)}
            className="w-20"
          />
          <span className="text-sm">{strokeWidth}px</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="cursor-pointer p-2 bg-gray-200 rounded hover:bg-gray-300">
            <Upload size={20} />
            <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
          </label>
          <button onClick={undo} className="p-2 bg-gray-200 rounded hover:bg-gray-300">
            <Undo size={20} />
          </button>
          <button onClick={redo} className="p-2 bg-gray-200 rounded hover:bg-gray-300">
            <Redo size={20} />
          </button>
          <button onClick={takeScreenshot} className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            <Download size={20} />
          </button>
          <button onClick={clearCanvas} className="p-2 bg-red-500 text-white rounded hover:bg-red-600">
            <Trash2 size={20} />
          </button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex-1 relative">
        <canvas
          ref={canvasRef}
          width={window.innerWidth - 335}
          height={window.innerHeight - 150}
          className="border border-gray-300 bg-white cursor-crosshair"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />

        {/* Sticky Notes */}
        {stickyNotes.map(note => (
          <div
            key={note.id}
            className="!absolute p-2 shadow-lg rounded cursor-move min-w-32 min-h-20"
            style={{
              left: note.x,
              top: note.y,
              backgroundColor: note.color
            }}
            draggable
            onDragEnd={(e) => {
              const rect = canvasRef.current.getBoundingClientRect();
              const newX = e.clientX - rect.left;
              const newY = e.clientY - rect.top;
              setStickyNotes(stickyNotes.map(n =>
                n.id === note.id ? { ...n, x: newX, y: newY } : n
              ));
            }}
          >
            <textarea
              value={note.text}
              onChange={(e) => {
                setStickyNotes(stickyNotes.map(n =>
                  n.id === note.id ? { ...n, text: e.target.value } : n
                ));
              }}
              className="w-full h-full bg-transparent border-none outline-none resize-none text-sm"
              placeholder="Type your note..."
            />
            <button
              onClick={() => setStickyNotes(stickyNotes.filter(n => n.id !== note.id))}
              className="!absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
            >
              ×
            </button>
          </div>
        ))}

        {/* Text Input Modal */}
        {showTextInput && (
          <div className="!absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Add Text</h3>
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Enter text..."
                className="w-full p-2 border rounded mb-4"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && addTextElement()}
              />
              <div className="flex gap-2">
                <button
                  onClick={addTextElement}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowTextInput(false)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Notes;